// src/config/permissions.js
// Role-based permission matrix for garage employees
// Each role defines what dashboard sections and features are accessible

export const EMPLOYEE_ROLES = {
  owner: { label: 'Owner', color: '#7C3AED', description: 'Full access to all features' },
  manager: { label: 'Manager', color: '#2563EB', description: 'Full access except settings and employee management' },
  technician: { label: 'Technician', color: '#059669', description: 'Jobs, inspections, and assigned appointments' },
  receptionist: { label: 'Receptionist', color: '#D97706', description: 'Scheduling, customers, quotes, and invoices' },
  jockey: { label: 'Jockey / Lot Attendant', color: '#6B7280', description: 'View today\'s appointments only' },
  accounting: { label: 'Accounting', color: '#DC2626', description: 'Financial data, invoices, and integrations' },
};

export const ROLE_PERMISSIONS = {
  owner: {
    dashboard: true,
    messages: true,
    appointments: true,
    jobs: true,
    job_board: true,
    inspections: true,
    job_templates: true,
    parts: true,
    quotes: true,
    invoicing: true,
    accounting: true,
    customers: true,
    task_board: true,
    service_reminders: true,
    analytics: true,
    marketing: true,
    landing_pages: true,
    promotions: true,
    social_media: true,
    content_library: true,
    leads: true,
    settings: true,
    employees: true,
    symptom_tree: true,
    booking_settings: true,
    service_bays: true,
    operating_hours: true,
    email_templates: true,
    integrations: true,
    view_revenue: true,
    view_costs: true,
    view_profit_margins: true,
    manage_employees: true,
    delete_records: true,
  },
  manager: {
    dashboard: true,
    messages: true,
    appointments: true,
    jobs: true,
    job_board: true,
    inspections: true,
    job_templates: true,
    parts: true,
    quotes: true,
    invoicing: true,
    accounting: false,
    customers: true,
    task_board: true,
    service_reminders: true,
    analytics: true,
    marketing: true,
    landing_pages: true,
    promotions: true,
    social_media: true,
    content_library: true,
    leads: true,
    settings: false,
    employees: false,
    symptom_tree: true,
    booking_settings: false,
    service_bays: false,
    operating_hours: false,
    email_templates: false,
    integrations: false,
    view_revenue: true,
    view_costs: true,
    view_profit_margins: true,
    manage_employees: false,
    delete_records: false,
  },
  technician: {
    dashboard: true,
    messages: false,
    appointments: true,
    jobs: true,
    job_board: true,
    inspections: true,
    job_templates: true,
    parts: true,
    quotes: false,
    invoicing: false,
    accounting: false,
    customers: false,
    task_board: false,
    service_reminders: false,
    analytics: false,
    marketing: false,
    landing_pages: false,
    promotions: false,
    social_media: false,
    content_library: false,
    leads: false,
    settings: false,
    employees: false,
    symptom_tree: false,
    booking_settings: false,
    service_bays: false,
    operating_hours: false,
    email_templates: false,
    integrations: false,
    view_revenue: false,
    view_costs: false,
    view_profit_margins: false,
    manage_employees: false,
    delete_records: false,
  },
  receptionist: {
    dashboard: true,
    messages: true,
    appointments: true,
    jobs: true,
    job_board: true,
    inspections: false,
    job_templates: true,
    parts: false,
    quotes: true,
    invoicing: true,
    accounting: false,
    customers: true,
    task_board: true,
    service_reminders: true,
    analytics: false,
    marketing: false,
    landing_pages: false,
    promotions: false,
    social_media: false,
    content_library: false,
    leads: true,
    settings: false,
    employees: false,
    symptom_tree: true,
    booking_settings: false,
    service_bays: false,
    operating_hours: false,
    email_templates: false,
    integrations: false,
    view_revenue: false,
    view_costs: false,
    view_profit_margins: false,
    manage_employees: false,
    delete_records: false,
  },
  jockey: {
    dashboard: true,
    messages: false,
    appointments: true,
    jobs: false,
    job_board: false,
    inspections: false,
    job_templates: false,
    parts: false,
    quotes: false,
    invoicing: false,
    accounting: false,
    customers: false,
    task_board: false,
    service_reminders: false,
    analytics: false,
    marketing: false,
    landing_pages: false,
    promotions: false,
    social_media: false,
    content_library: false,
    leads: false,
    settings: false,
    employees: false,
    symptom_tree: false,
    booking_settings: false,
    service_bays: false,
    operating_hours: false,
    email_templates: false,
    integrations: false,
    view_revenue: false,
    view_costs: false,
    view_profit_margins: false,
    manage_employees: false,
    delete_records: false,
  },
  accounting: {
    dashboard: true,
    messages: false,
    appointments: false,
    jobs: false,
    job_board: false,
    inspections: false,
    job_templates: false,
    parts: true,
    quotes: true,
    invoicing: true,
    accounting: true,
    customers: true,
    task_board: false,
    service_reminders: false,
    analytics: true,
    marketing: false,
    landing_pages: false,
    promotions: false,
    social_media: false,
    content_library: false,
    leads: false,
    settings: false,
    employees: false,
    symptom_tree: false,
    booking_settings: false,
    service_bays: false,
    operating_hours: false,
    email_templates: false,
    integrations: false,
    view_revenue: true,
    view_costs: true,
    view_profit_margins: true,
    manage_employees: false,
    delete_records: false,
  },
};

// Map dashboard menu item IDs to permission keys
export const MENU_PERMISSION_MAP = {
  'dashboard': 'dashboard',
  'messages': 'messages',
  'appointments': 'appointments',
  'symptom-tree': 'symptom_tree',
  'jobs': 'jobs',
  'job-board': 'job_board',
  'inspections': 'inspections',
  'job-templates': 'job_templates',
  'parts': 'parts',
  'quotes': 'quotes',
  'invoicing': 'invoicing',
  'accounting': 'accounting',
  'customer-database': 'customers',
  'task-board': 'task_board',
  'service-reminders': 'service_reminders',
  'analytics': 'analytics',
  'landing-pages': 'landing_pages',
  'promotions': 'promotions',
  'social-media': 'social_media',
  'content-library': 'content_library',
  'leads': 'leads',
  'profile': 'settings',
  'services': 'settings',
  'booking-settings': 'booking_settings',
  'employees': 'employees',
  'technicians': 'employees',
  'service-bays': 'service_bays',
  'operating-hours': 'operating_hours',
  'email-templates': 'email_templates',
  'integrations': 'integrations',
};

/**
 * Get effective permissions for a role, merged with custom overrides
 * @param {string} role - Employee role
 * @param {object|null} customPermissions - Per-employee overrides
 * @returns {object} Merged permissions
 */
export const getEffectivePermissions = (role, customPermissions = null) => {
  const base = ROLE_PERMISSIONS[role] || ROLE_PERMISSIONS.jockey;
  if (!customPermissions) return { ...base };
  return { ...base, ...customPermissions };
};
